'use client';

import { Heart } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/shared/lib/cn';
import { useConceptFavorite } from './use-concept-favorite';

interface FavoriteButtonProps {
  projectId: string;
  conceptId: string;
  isFavorite: boolean;
  /** Optional: customize size */
  size?: 'sm' | 'default' | 'icon';
  /** Optional: customize variant */
  variant?: 'ghost' | 'outline' | 'default';
  /** Optional: additional class names */
  className?: string;
  /**
   * Optional: external click handler. If provided, this is called instead of
   * the internal mutation. Use this when parent controls the toggle logic.
   */
  onToggle?: (conceptId: string, currentIsFavorite: boolean) => void;
  /** Optional: callback after successful internal toggle */
  onToggled?: (newIsFavorite: boolean) => void;
}

/**
 * Unified Favorite button component.
 *
 * Two modes:
 * 1. Autonomous mode (default): Uses the shared `useConceptFavorite` hook to
 *    toggle favorite (optimistic + debounced mutation).
 * 2. Callback mode: When `onToggle` is provided, calls it instead of the
 *    internal mutation.
 */
export function FavoriteButton({
  projectId,
  conceptId,
  isFavorite,
  size = 'icon',
  variant = 'ghost',
  className,
  onToggle,
  onToggled,
}: FavoriteButtonProps) {
  const { isFavorite: optimisticFavorite, toggle } = useConceptFavorite({
    projectId,
    conceptId,
    isFavorite,
    onToggled,
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Callback mode: let parent handle the toggle
    if (onToggle) {
      onToggle(conceptId, optimisticFavorite);
      return;
    }

    toggle();
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      className={cn('text-muted-foreground hover:text-foreground', className)}
      aria-label={
        optimisticFavorite ? 'Remove from favorites' : 'Add to favorites'
      }
    >
      <Heart
        className={cn(
          'h-4 w-4 transition-colors',
          optimisticFavorite ? 'fill-primary text-primary' : ''
        )}
      />
    </Button>
  );
}
